/**
 * Data hook for searching suppliers when creating a chat
 * Follows GraphQL separation pattern - handles GraphQL operations and maps to domain types
 */

import type { UserSelectOption } from '@choco-admin/types';
import { useCallback, useMemo } from 'react';

import { useSearchSuppliersForChatLazyQuery } from '../../../generated';
import { usePuppyMode } from '../../../hooks';
import { mapToSupplierSelectOption } from '../utils/createChatMapper';

export interface UseSearchSuppliersForChatConfig {
  captureException?: (error: Error) => void;
}

export interface UseSearchSuppliersForChatResponse {
  suppliers: UserSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchSuppliers: (searchTerm: string) => Promise<void>;
  clearResults: () => void;
}

export const useSearchSuppliersForChat = (
  config: UseSearchSuppliersForChatConfig = {}
): UseSearchSuppliersForChatResponse => {
  const { captureException } = config;
  const { puppyFilter } = usePuppyMode();

  const [executeSearch, { data, loading, error }] =
    useSearchSuppliersForChatLazyQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    });

  const searchSuppliers = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) return;

      try {
        const filter: any = {};

        // Apply puppy filter if set
        if (puppyFilter !== undefined) {
          filter.isPuppy = puppyFilter;
        }

        await executeSearch({
          variables: {
            keywords: searchTerm.trim(),
            filter: Object.keys(filter).length > 0 ? filter : undefined,
          },
        });
      } catch (err) {
        const suppliersError = err as Error;
        captureException?.(suppliersError);
        throw suppliersError;
      }
    },
    [executeSearch, puppyFilter, captureException]
  );

  const clearResults = useCallback(() => {
    // Clear results by executing a search with empty term
    executeSearch({
      variables: {
        keywords: '',
      },
    });
  }, [executeSearch]);

  const suppliers = useMemo(() => {
    const supplierSearch = data?.supplierSearch;

    if (
      !supplierSearch ||
      supplierSearch.__typename !== 'SearchSupplierConnection'
    ) {
      return [];
    }

    return (
      supplierSearch.edges?.map((edge: any) =>
        mapToSupplierSelectOption(edge.node)
      ) || []
    );
  }, [data]);

  return {
    suppliers,
    isLoading: loading,
    error: error || null,
    searchSuppliers,
    clearResults,
  };
};
